/**
 * Project entity — projects.Project (xsite/projects/models.py, serializers.py).
 *
 * `useProjects` is the infinite-scroll catalog list (search + ordering,
 * matches Projects.jsx). `useProject` is the single-record detail view;
 * items/offers/tasks/equipment/cases manage their own realtime-synced
 * queries in their own sub-components (see ProjectDetail.jsx), so they are
 * deliberately NOT listed as detailRelatedModels here — only staff/vehicle
 * changes affect the header stats shown on the detail page itself.
 *
 * `projectApi` extends the standard CRUD client with the trash/restore/
 * permanent-delete actions (ProjectViewSet.trash/restore/permanent_delete),
 * and `projectKeys` (from createResourceHooks) keeps their cache keys
 * consistent with the rest of this resource's queries.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../shared/api';
import { createResourceApi, type ResourceId } from '../shared/resourceApi';
import { createResourceHooks } from '../shared/createResourceHooks';
import type { Status, IdName, Currency } from '../shared/types';

export interface Project {
  id: number;
  name: string;
  customer?: IdName | null;
  customer_name?: string | null;
  status?: Status | null;
  type?: IdName | null;
  currency?: Currency | null;
  start_date?: string | null;
  end_date?: string | null;
  location?: string | null;
  budget?: number | string | null;
  locked?: boolean;
  is_deleted?: boolean;
  deleted_at?: string | null;
}

const PROJECTS_BASE = 'projects/api/projects';

export const projectApi = {
  ...createResourceApi<Project>(PROJECTS_BASE),
  /** GET <base>/trash/ — projects currently in the trash. */
  trash: (): Promise<Project[]> => api.get<Project[]>(`${PROJECTS_BASE}/trash/`),
  /** PATCH <base>/<id>/restore/ — take a project out of the trash. */
  restore: (id: ResourceId): Promise<Project> => api.patch<Project>(`${PROJECTS_BASE}/${id}/restore/`, {}),
  /** DELETE <base>/<id>/permanent/ — permanently delete a project already in the trash. */
  permanentDelete: (id: ResourceId): Promise<void> =>
    api.delete(`${PROJECTS_BASE}/${id}/permanent/`) as Promise<void>,
};

export const {
  keys: projectKeys,
  useInfiniteList: useProjects,
  useDetail: useProject,
  useCreate: useCreateProject,
  useUpdate: useUpdateProject,
  useRemove: useDeleteProject,
} = createResourceHooks(projectApi, {
  name: 'projects',
  model: 'projects.project',
  detailRelatedModels: ['projects.projectstaff', 'projects.projectvehicle'],
  detailDebounceMs: 400,
});

const trashKey = () => [...projectKeys.all, 'trash'];

/** Projects currently in the trash. */
export function useProjectTrash() {
  return useQuery({
    queryKey: trashKey(),
    queryFn: () => projectApi.trash(),
  });
}

export function useRestoreProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: ResourceId) => projectApi.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trashKey() });
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
    },
  });
}

export function usePermanentlyDeleteProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: ResourceId) => projectApi.permanentDelete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trashKey() }),
  });
}
